import React from 'react';
import { Box } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { theme, ThemedBox, ThemedTypography } from '../../themes/technicalAlerts';

const FilterPills = ({ 
  filters = {}, 
  onRemoveFilter,
  onClearAll,
  columnConfigs = {},
  filterData = {}
}) => {
  const { t } = useTranslation();
  
  // Convert filters object to array of pills
  const filterPills = React.useMemo(() => {
    const pills = [];
    
    Object.entries(filters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      
      const columnConfig = columnConfigs[columnKey];
      const columnTitle = columnConfig?.title || columnKey;
      
      values.forEach(value => {
        let displayValue = value;
        const formatter = columnConfig?.valueFormatter;
        if (typeof formatter === 'function') {
          displayValue = formatter(value, { filterData, t });
        }
        
        // Special handling for product name display
        if (columnKey === 'productName' && filterData?.products) {
          const product = filterData.products.find(p => String(p.id) === String(value));
          displayValue = product ? product.name : displayValue;
        }

        // Special handling for route display
        if (columnKey === 'route' && Array.isArray(filterData?.routes)) {
          const route = filterData.routes.find(routeOption => {
            const routeId = routeOption?.id ?? routeOption?.value ?? routeOption?.name;
            return String(routeId) === String(value);
          });
          if (route) {
            displayValue = route.name || route.code || route.label || displayValue;
          }
        }
        
        pills.push({
          id: `${columnKey}-${value}`,
          columnKey,
          value, // Keep original ID for removal
          label: `${columnTitle}: ${displayValue}` // Show name in display
        });
      });
    });
    
    return pills;
  }, [filters, columnConfigs, filterData]);

  return (
    <Box sx={{ 
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      alignItems: 'center',
      minHeight: '32px',
      py: 0.5
    }}>
      {filterPills.map(pill => (
        <Box
          key={pill.id}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '28px',
            padding: `0 ${theme.spacing.lg}`,
            backgroundColor: theme.colors.primaryBackground,
            border: `1px solid ${theme.colors.primaryBorder}`,
            borderRadius: '16px',
            gap: 0.75,
            transition: theme.transitions.normal,
            '&:hover': {
              backgroundColor: 'rgba(0, 122, 255, 0.12)',
              borderColor: 'rgba(0, 122, 255, 0.3)',
              transform: 'translateY(-1px)',
              boxShadow: theme.shadows.sm,
            }
          }}
        >
          <ThemedTypography.Body2 sx={{ 
            fontWeight: 600,
            color: theme.colors.primary,
            letterSpacing: '0.02em' 
          }}>
            {pill.label}
          </ThemedTypography.Body2>
          <Close
            sx={{ 
              fontSize: 16,
              cursor: 'pointer',
              color: theme.colors.primary,
              opacity: 0.7,
              marginLeft: theme.spacing.xs,
              transition: theme.transitions.fast,
              '&:hover': {
                opacity: 1,
              }
            }}
            onClick={() => onRemoveFilter(pill.columnKey, pill.value)}
          />
        </Box>
      ))}
      
      {/* Clear All button - only show when there are active filters */}
      {filterPills.length > 1 && (
        <Box
          onClick={onClearAll}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '28px',
            padding: `0 ${theme.spacing.lg}`,
            backgroundColor: theme.colors.background.hover,
            border: `1px solid ${theme.colors.border.medium}`,
            borderRadius: '16px',
            cursor: 'pointer',
            transition: theme.transitions.normal,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              borderColor: theme.colors.border.dark,
              transform: 'translateY(-1px)',
            }
          }}
        >
          <ThemedTypography.Body2 sx={{
            fontWeight: 500,
            color: theme.colors.text.muted,
            letterSpacing: '0.02em'
          }}>
            {t('clearAll') || 'Clear all'}
          </ThemedTypography.Body2>
        </Box>
      )}
    </Box>
  );
};

export default FilterPills;
